import api from './axios';

// Generic CRUD service factory
export const createService = (endpoint) => ({
  getAll: (params) => api.get(`/${endpoint}`, { params }),
  getById: (id) => api.get(`/${endpoint}/${id}`),
  create: (data) => api.post(`/${endpoint}`, data),
  update: (id, data) => api.put(`/${endpoint}/${id}`, data),
  delete: (id) => api.delete(`/${endpoint}/${id}`),
});

export const authService = {
  login: (data) => api.post('/auth/login', data),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
};

export const dashboardService = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getSalesChart: (params) => api.get('/dashboard/sales-chart', { params }),
};

export const medicineService = {
  ...createService('medicines'),
  search: (q) => api.get('/medicines/search', { params: { q } }),
  searchWithBatches: (q) => api.get('/medicines/search-with-batches', { params: { q } }),
};

export const inventoryService = {
  getAll:          (params)           => api.get('/inventory', { params }),
  getOverview:     (params)           => api.get('/inventory/overview', { params }),
  stockIn:         (data)             => api.post('/inventory/stock-in', data),
  stockOut:        (data)             => api.post('/inventory/stock-out', data),
  getTransactions: (params)           => api.get('/inventory/transactions', { params }),
  deleteRecord:    (id)                => api.delete(`/inventory/summary/${id}`),
  updateRecord:    (id, data)         => api.put(`/inventory/summary/${id}`, data), 
  getStats:        (medicineId, params) => api.get(`/inventory/stats/${medicineId}`, { params }),
};

export const purchaseService = {
  ...createService('purchases'),
  getById:      (id)       => api.get(`/purchases/${id}`),
  getStats:     (params)   => api.get('/purchases/stats', { params }),
  approve:      (id)       => api.put(`/purchases/${id}/approve`),
  receive:      (id)       => api.put(`/purchases/${id}/receive`),
  cancel:       (id, data) => api.put(`/purchases/${id}/cancel`, data),
  recordPayment:(id, data) => api.post(`/purchases/${id}/payment`, data),
  uploadImages: (id, fd)   => api.post(`/purchases/${id}/images`, fd, { headers:{ 'Content-Type':'multipart/form-data' } }),
};
export const supplierPaymentService = {
  getPayments: (supplierId, params) => api.get(`/suppliers/${supplierId}/payments`, { params }),
  addPayment:  (supplierId, data)   => api.post(`/suppliers/${supplierId}/payments`, data),
};
export const saleService = {
  ...createService('sales'),
  getById:       (id)       => api.get(`/sales/${id}`),
  recordPayment: (id, data) => api.post(`/sales/${id}/payment`, data),
};
export const supplierService = {
  ...createService('suppliers'),
  search:  (q)  => api.get('/suppliers/search', { params: { q } }),
  getById: (id) => api.get(`/suppliers/${id}`),
};
export const customerService = createService('customers');
export const patientService = createService('patients');
export const doctorService = { ...createService('employees'), getAll: (p) => api.get('/employees', { params: p }) }; // doctors → employees
export const prescriptionService = createService('prescriptions');
export const userService = createService('users');
export const roleService = createService('roles');
export const branchService = createService('branches');
export const categoryService = createService('categories');
export const expenseService = createService('expenses');
export const notificationService = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
};
export const auditService = {
  getAll: (params) => api.get('/audit', { params }),
};
export const reportService = {
  getSales: (params) => api.get('/reports/sales', { params }),
  getPurchases: (params) => api.get('/reports/purchases', { params }),
  getInventory: (params) => api.get('/reports/inventory', { params }),
  getExpiry: (params) => api.get('/reports/expiry', { params }),
  getProfit: (params) => api.get('/reports/profit', { params }),
};
export const accountingService = {
  getSummary:    (params) => api.get('/accounting/summary',     { params }),
  getProfitLoss: (params) => api.get('/accounting/profit-loss', { params }),
  getCashFlow:   (params) => api.get('/accounting/cash-flow',   { params }),

  // Customer accounts
  getCustomerAccounts: (params)     => api.get('/accounting/customers', { params }),
  getCustomerInvoices: (id)         => api.get(`/accounting/customers/${id}/invoices`),
  getCustomerPayments: (id, params) => api.get(`/accounting/customers/${id}/payments`, { params }),
  addCustomerPayment:  (id, data)   => api.post(`/accounting/customers/${id}/payments`, data),

  // Supplier accounts
  getSupplierAccounts: (params)     => api.get('/accounting/suppliers', { params }),
  getSupplierInvoices: (id)         => api.get(`/accounting/suppliers/${id}/invoices`),
  getSupplierPayments: (id, params) => api.get(`/accounting/suppliers/${id}/payments`, { params }),
  addSupplierPayment:  (id, data)   => api.post(`/accounting/suppliers/${id}/payments`, data),

  // Expenses
  getExpenses:   (params) => api.get('/accounting/expenses',     { params }),
  createExpense: (data)   => api.post('/accounting/expenses',    data),
  updateExpense: (id, d)  => api.put(`/accounting/expenses/${id}`, d),
  deleteExpense: (id)     => api.delete(`/accounting/expenses/${id}`),
};

// NEW services
export const brandService = createService('brands');
export const manufacturerService = createService('manufacturers');
export const strengthService = createService('strengths');
export const taxService = createService('taxes');
export const currencyService = createService('currencies');
export const bankService = {
  ...createService('bank-accounts'),
  getTransactions: (id, params) => api.get(`/bank-accounts/${id}/transactions`, { params }),
  addTransaction: (id, data) => api.post(`/bank-accounts/${id}/transactions`, data),
};
export const requisitionService = {
  ...createService('requisitions'),
  approve: (id) => api.put(`/requisitions/${id}/approve`),
  reject: (id) => api.put(`/requisitions/${id}/reject`),
};
export const settingsService = {
  getAll: (params) => api.get('/settings', { params }),
  updateAll: (data) => api.put('/settings', data),
  update: (key, data) => api.put(`/settings/${key}`, data),
};

export const employeeService = {
  ...createService('employees'),
  getSalaryHistory:  (id)       => api.get(`/employees/${id}/salary-history`),
  calculateSalary:   (id, data) => api.post(`/employees/${id}/calculate-salary`, data),
  paySalary:         (id, data) => api.post(`/employees/${id}/pay-salary`, data),
};

export const purchaseRequestService = {
  ...createService('purchase-requests'),
  getOutOfStock: (params)      => api.get('/purchase-requests/out-of-stock', { params }),
  convert:       (id, data)    => api.post(`/purchase-requests/${id}/convert`, data),
  cancel:        (id)          => api.put(`/purchase-requests/${id}/cancel`),
};

export const partnerService = {
  getSummary: ()     => api.get('/partners/summary'),
  getByPartner:(key) => api.get(`/partners/${key}`),
  repay:      (data) => api.post('/partners/repay', data),
};
